/**
 * 🎭 GUIDED TOUR - Visite guidée avec Pixie en MODE GRAND VISAGE
 * 
 * Pixie te prend par la main et te montre tout Heartbeats !
 * - Design "Grand Visage" immersif
 * - Overlay avec spotlight sur l'élément ciblé
 * - Messages contextuels de Pixie
 * - Navigation fluide entre les étapes
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, X, Sparkles } from 'lucide-react';
import { GUIDE_IMAGES, GUIDE_CONFIG } from './GuideAvatar';
import { useGuideIntelligence, TOUR_STEPS } from './GuideIntelligence';

export function GuidedTour({ customerName }) {
  const { currentTourStep, nextTourStep, tourStep } = useGuideIntelligence();
  const [imageLoaded, setImageLoaded] = useState(false);
  
  if (!currentTourStep) return null;
  
  const step = currentTourStep;
  const progress = ((tourStep + 1) / TOUR_STEPS.length) * 100;
  
  // Remplacer {name} dans le message
  const message = step.message.replace(/{name}/g, customerName || 'toi');
  
  // Configuration du guide
  const guideId = step.guide || 'pixie';
  const config = GUIDE_CONFIG[guideId] || GUIDE_CONFIG.pixie;
  const images = GUIDE_IMAGES[guideId] || GUIDE_IMAGES.pixie;
  const imageUrl = images[step.expression] || images.default;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100]"
      >
        {/* Overlay sombre avec gradient radial */}
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at 50% 30%, ${config.bgGlow}40 0%, rgba(0,0,0,0.85) 60%)`
          }}
        />
        
        {/* Spotlight sur l'élément ciblé */}
        {step.target && step.highlight && (
          <SpotlightEffect target={step.target} />
        )}
        
        {/* 🎭 CARTE GRAND VISAGE */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          className="fixed bottom-8 left-4 right-4 sm:left-auto sm:right-8 sm:max-w-md z-[101]"
        >
          <div 
            className="relative rounded-3xl overflow-hidden shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${config.bgGlow}30 0%, rgba(17, 24, 39, 0.98) 50%, ${config.bgGlow}20 100%)`,
              boxShadow: `0 25px 80px ${config.bgGlow}, 0 0 60px rgba(168, 85, 247, 0.3)`,
            }}
          >
            {/* Barre de progression */}
            <div className="h-1.5 bg-gray-800/50">
              <motion.div
                className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-fuchsia-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            
            {/* 🖼️ SECTION GRAND VISAGE */}
            <div className="relative p-4">
              <div className="flex items-center gap-4">
                {/* Grand visage animé */}
                <motion.div 
                  className="relative flex-shrink-0"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                >
                  {/* Glow effect */}
                  <div 
                    className="absolute inset-0 rounded-full blur-2xl opacity-60"
                    style={{ background: config.bgGlow }}
                  />
                  
                  {/* Image du guide - PLUS GRANDE */}
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-white/30 shadow-xl">
                    {imageUrl && (
                      <motion.img
                        src={imageUrl}
                        alt={config.name}
                        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5 }}
                      />
                    )}
                    
                    {/* Effet de brillance */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent"
                      animate={{ x: [-100, 150] }}
                      transition={{ duration: 3, repeat: Infinity, repeatDelay: 4 }}
                    />
                  </div>
                  
                  {/* Badge du guide */}
                  <motion.div 
                    className="absolute -bottom-1 -right-1 px-2.5 py-1 rounded-full text-white text-xs font-bold shadow-lg flex items-center gap-1"
                    style={{ 
                      background: `linear-gradient(135deg, #ec4899 0%, #a855f7 100%)` 
                    }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Sparkles className="w-3 h-3" />
                    {config.name}
                  </motion.div>
                </motion.div>
                
                {/* Infos du guide */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg">{step.title}</h3>
                  <p className="text-pink-400 text-sm font-medium">
                    Étape {tourStep + 1}/{TOUR_STEPS.length}
                  </p>
                </div>
                
                {/* Bouton fermer */}
                <button
                  onClick={() => nextTourStep()}
                  className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  title="Passer la visite"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Message */}
            <div className="px-5 pb-2">
              <motion.p 
                className="text-gray-200 text-base leading-relaxed"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {message}
              </motion.p>
            </div>
            
            {/* Action */}
            {step.action && (
              <div className="px-5 pb-5 pt-3">
                <motion.button
                  onClick={nextTourStep}
                  className="w-full py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-pink-500 via-purple-500 to-fuchsia-500 hover:opacity-90 flex items-center justify-center gap-2 text-base shadow-lg"
                  style={{ boxShadow: `0 10px 30px ${config.bgGlow}` }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {step.action.label}
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </div>
            )}
          </div>
          
          {/* Particules magiques améliorées */}
          <FloatingParticles color={config.bgGlow} />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * ✨ Particules flottantes décoratives (générées côté client uniquement)
 */
function FloatingParticles({ color = '#ec4899', count = 12 }) {
  const [particles, setParticles] = React.useState([]);
  
  React.useEffect(() => {
    const generated = Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: 2 + Math.random() * 2,
      delay: Math.random() * 2,
    }));
    setParticles(generated);
  }, [count]);
  
  if (particles.length === 0) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute w-2 h-2 rounded-full"
          style={{
            background: `linear-gradient(135deg, ${color}, #8B5CF6)`,
            left: `${particle.left}%`,
            top: `${particle.top}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
          }}
        />
      ))}
    </div>
  );
}

/**
 * 🔦 SpotlightEffect - Effet spotlight sur un élément
 */
function SpotlightEffect({ target }) {
  const [rect, setRect] = React.useState(null);
  
  React.useEffect(() => {
    const element = document.querySelector(target);
    if (element) {
      const r = element.getBoundingClientRect();
      setRect({
        top: r.top - 8,
        left: r.left - 8,
        width: r.width + 16,
        height: r.height + 16,
      });
    }
  }, [target]);
  
  if (!rect) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="absolute rounded-2xl ring-4 ring-pink-500 ring-offset-4 ring-offset-transparent z-[99]"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 30px rgba(236, 72, 153, 0.5)',
      }}
    >
      {/* Pulse animation */}
      <motion.div
        className="absolute inset-0 rounded-2xl border-2 border-pink-400"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [1, 0.5, 1],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
        }}
      />
    </motion.div>
  );
}

export default GuidedTour;
